// src/components/fees/Reports.js
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Grid, Chip, Pagination, Alert, Button, Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  AttachMoney as MoneyIcon,
  Payment as PaymentIcon,
  AccountBalance as BalanceIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as StatusIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon,
  Category as CategoryIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { feeAPI, academicAPI } from '../../services/api';

const Reports = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feeGroups, setFeeGroups] = useState([]);
  const [classStreams, setClassStreams] = useState([]);
  const [terms, setTerms] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 15 });
  const [filters, setFilters] = useState({
    fee_group_id: '',
    class_stream_id: '',
    status: '',
    term_id: ''
  });
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchFeeGroups();
    fetchClassStreams();
    fetchTerms();
    fetchReports();
  }, []);

  useEffect(() => {
    fetchReports(1);
  }, [filters]);

  const fetchFeeGroups = async () => {
    try {
      const response = await feeAPI.getGroups({ per_page: 1000 });
      const groups = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setFeeGroups(groups);
    } catch (error) {
      console.error('Error fetching fee groups:', error);
    }
  };

  const fetchClassStreams = async () => {
    try {
      const response = await feeAPI.getClassStreams();
      console.log('Class streams response:', response.data);
      const streams = Array.isArray(response.data) ? response.data : response.data.data || [];
      console.log('Processed class streams:', streams);
      setClassStreams(streams);
    } catch (error) {
      console.error('Error fetching class streams:', error);
    }
  };

  const fetchTerms = async () => {
    try {
      const response = await academicAPI.getTerms();
      setTerms(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch (error) {
      console.error('Error fetching terms:', error);
    }
  };

  const fetchReports = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, per_page: pagination.per_page };
      if (filters.fee_group_id && filters.fee_group_id !== '') params.fee_group_id = filters.fee_group_id;
      if (filters.class_stream_id && filters.class_stream_id !== '') params.class_stream_id = filters.class_stream_id;
      if (filters.status && filters.status !== '') params.status = filters.status;
      if (filters.term_id && filters.term_id !== '') params.term_id = filters.term_id;

      const response = await feeAPI.getReports(params);
      const data = response.data;
      setReportData(data.data || []);
      setPagination(prev => ({
        ...prev,
        current_page: data.current_page || 1,
        last_page: data.last_page || 1,
        total: data.total || 0,
        per_page: data.per_page || prev.per_page
      }));
    } catch (error) {
      console.error('Error fetching reports:', error);
      setError(error.response?.data?.message || 'Failed to load reports');
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
    fetchReports(1);
  };

  const handleClearFilters = () => {
    setFilters({
      fee_group_id: '',
      class_stream_id: '',
      status: '',
      term_id: ''
    });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handlePageChange = (event, page) => {
    setPagination(prev => ({ ...prev, current_page: page }));
    fetchReports(page);
  };

  const handlePageSizeChange = (newSize) => {
    setPagination(prev => ({ ...prev, per_page: newSize, current_page: 1 }));
    fetchReports(1);
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloading(true);
      const response = await feeAPI.downloadReportsPdf(filters);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fee_reports_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const hasActiveFilters = filters.fee_group_id || filters.class_stream_id || filters.status || filters.term_id;

  return (
    <Box>
      <Box mb={4}>
        <Grid container justifyContent="space-between" alignItems="center" mb={3}>
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1.5rem' }}>
              Fee Reports
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              View and filter fee allocation reports by fee group with real-time data
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} mt={{ xs: 2, md: 0 }}>
              <Button
                variant="outlined"
                startIcon={<PdfIcon />}
                onClick={handleDownloadPdf}
                disabled={downloading}
              >
                {downloading ? 'Downloading...' : 'Download PDF'}
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon sx={{ fontSize: 18 }} />
              Filter Reports
            </Typography>
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2}>

                {/* Fee Group */}
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <Select
                      value={filters.fee_group_id}
                      onChange={e => handleFilterChange('fee_group_id', e.target.value)}
                      label="Fee Group"
                      displayEmpty
                    >
                      <MenuItem value="">All Fee Groups</MenuItem>
                      {feeGroups.map(fg => (
                        <MenuItem key={fg.id} value={fg.id}>{fg.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Class Stream */}
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <Select
                      value={filters.class_stream_id}
                      onChange={e => handleFilterChange('class_stream_id', e.target.value)}
                      label="Class Stream"
                      displayEmpty
                    >
                      <MenuItem value="">All Classes</MenuItem>
                      {classStreams.map(cs => (
                        <MenuItem key={cs.id} value={cs.id}>{cs.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Payment Status */}
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <Select
                      value={filters.status}
                      onChange={e => handleFilterChange('status', e.target.value)}
                      label="Payment Status"
                      displayEmpty
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="partial">Partial</MenuItem>
                      <MenuItem value="unpaid">Unpaid</MenuItem>
                      <MenuItem value="overdue">Overdue</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Term */}
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <Select
                      value={filters.term_id}
                      onChange={e => handleFilterChange('term_id', e.target.value)}
                      label="Term"
                      displayEmpty
                    >
                      <MenuItem value="">All Terms</MenuItem>
                      {terms.map(term => (
                        <MenuItem key={term.id} value={term.id}>{term.name} {term.year}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

              </Grid>

              {hasActiveFilters && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<ClearIcon />}
                    onClick={handleClearFilters}
                  >
                    Clear Filters
                  </Button>
                </Box>
              )}

              {hasActiveFilters && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>Active Filters:</strong>
                  {filters.fee_group_id && ` Fee Group: ${feeGroups.find(f => f.id == filters.fee_group_id)?.name}`}
                  {filters.class_stream_id && ` | Class: ${classStreams.find(c => c.id == filters.class_stream_id)?.name}`}
                  {filters.status && ` | Status: ${filters.status}`}
                  {filters.term_id && ` | Term: ${terms.find(t => t.id == filters.term_id)?.name}`}
                </Alert>
              )}
            </Box>

          </CardContent>
        </Card>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon sx={{ fontSize: 18 }} />
              Fee Report ({pagination.total} records)
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table>
              <TableHead>
                <TableRow sx={{
                  bgcolor: '#f8fafc',
                  '& .MuiTableCell-root': {
                    borderBottom: '2px solid #e2e8f0',
                    fontWeight: 600,
                    color: '#475569',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }
                }}>
                  <TableCell sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PersonIcon sx={{ fontSize: 16 }} /> Student
                    </Box>
                  </TableCell>
                  <TableCell sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <BadgeIcon sx={{ fontSize: 16 }} /> Reg No
                    </Box>
                  </TableCell>
                  <TableCell sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CategoryIcon sx={{ fontSize: 16 }} /> Fee Group
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                      <MoneyIcon sx={{ fontSize: 16 }} /> Amount
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                      <PaymentIcon sx={{ fontSize: 16 }} /> Paid
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                      <BalanceIcon sx={{ fontSize: 16 }} /> Balance
                    </Box>
                  </TableCell>
                  <TableCell sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <StatusIcon sx={{ fontSize: 16 }} /> Status
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No data found
                        {hasActiveFilters && ' for the selected filters'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  reportData.map((row, idx) => (
                    <TableRow key={idx} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {row.student_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="primary">
                          {row.registration_no}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={row.fee_group} size="small" variant="outlined" color="primary" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          UGX {parseFloat(row.amount).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500} color="success.main">
                          UGX {parseFloat(row.paid).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500} color={parseFloat(row.balance) > 0 ? 'error.main' : 'success.main'}>
                          UGX {parseFloat(row.balance).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.status}
                          size="small"
                          color={
                            row.status === 'paid' ? 'success' :
                            row.status === 'partial' ? 'warning' : 'error'
                          }
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination.last_page > 1 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
                p: 2,
                borderTop: '1px solid #e2e8f0'
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {pagination.total === 0 ? 0 : (pagination.current_page - 1) * pagination.per_page + 1} to{" "}
                  {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} entries
                </Typography>
                
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Rows per page:
                  </Typography>
                  <FormControl size="small">
                    <Select
                      value={pagination.per_page}
                      onChange={(e) => handlePageSizeChange(e.target.value)}
                      sx={{ minWidth: 70 }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={15}>15</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>

              <Pagination
                count={pagination.last_page}
                page={pagination.current_page}
                onChange={handlePageChange}
                color="primary"
                size="medium"
                siblingCount={1}
                boundaryCount={1}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Reports;